import React, { useEffect, useRef, useCallback, useState } from 'react';
import styles from './CaseIntelligenceVibrantPage.module.scss';
import { PageState } from './types';
import { resolvePageState } from './utils/stateResolver';
import { useAcceptCaseTagSuggestion } from '../redux/acceptCaseTagSuggestion';
import { useFetchCaseArtefacts } from '../redux/fetchCaseArtefacts';
import { useFetchCaseTagSuggestions } from '../redux/fetchCaseTagSuggestions';
import { useRejectCaseTagSuggestion } from '../redux/rejectCaseTagSuggestion';
import { useTriggerCrawl } from '../redux/triggerCrawl';
import CaseIntelligenceHeader from './components/CaseIntelligenceHeader';
import LoadingSkeleton from './components/LoadingSkeleton';
import NoAccessBanner from './components/NoAccessBanner';
import EmptyState from './components/EmptyState';
import CaseIntelligenceDashboard from './components/CaseIntelligenceDashboard';
import CrawlSummaryDrawer from './components/CrawlSummaryDrawer';

const CaseIntelligenceVibrantPage: React.FC = () => {
  const { caseArtefacts, caseArtefactsPending, caseArtefactsFeedback, fetchCaseArtefacts } =
    useFetchCaseArtefacts();
  const {
    caseTagSuggestions,
    caseTagSuggestionsPending,
    caseTagSuggestionsFeedback,
    fetchCaseTagSuggestions,
  } = useFetchCaseTagSuggestions();
  const { triggerCrawl, triggerCrawlPending } = useTriggerCrawl();
  const { acceptCaseTagSuggestion } = useAcceptCaseTagSuggestion();
  const { rejectCaseTagSuggestion } = useRejectCaseTagSuggestion();

  const [isCrawlSummaryOpen, setIsCrawlSummaryOpen] = useState(false);
  const [showScrollChrome, setShowScrollChrome] = useState(false);
  const scrollChromeTimeoutRef = useRef<number | null>(null);

  useEffect(() => {
    fetchCaseArtefacts().catch(() => {});
    fetchCaseTagSuggestions().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const caseArtefactsError = caseArtefactsFeedback?.error?.status ?? null;

  const pageState: PageState = resolvePageState({
    caseArtefacts,
    caseArtefactsPending,
    caseArtefactsError,
  });

  useEffect(() => {
    return () => {
      if (scrollChromeTimeoutRef.current) {
        window.clearTimeout(scrollChromeTimeoutRef.current);
      }
    };
  }, []);

  const handleRegenerate = useCallback(() => {
    triggerCrawl().catch(() => {});
  }, [triggerCrawl]);

  const handleOpenCrawlSummary = useCallback(() => {
    setIsCrawlSummaryOpen(true);
  }, []);

  const handleCloseCrawlSummary = useCallback(() => {
    setIsCrawlSummaryOpen(false);
  }, []);

  const handlePageScroll = useCallback(() => {
    setShowScrollChrome(true);

    if (scrollChromeTimeoutRef.current) {
      window.clearTimeout(scrollChromeTimeoutRef.current);
    }

    scrollChromeTimeoutRef.current = window.setTimeout(() => {
      setShowScrollChrome(false);
      scrollChromeTimeoutRef.current = null;
    }, 900);
  }, []);

  const handleRetryTags = useCallback(() => {
    fetchCaseTagSuggestions().catch(() => {});
  }, [fetchCaseTagSuggestions]);

  return (
    <div
      className={`${styles.page} ${showScrollChrome ? styles.pageScrolling : ''}`}
      onScroll={handlePageScroll}
    >
      <CaseIntelligenceHeader
        triggerCrawlPending={triggerCrawlPending}
        onRegenerate={handleRegenerate}
        onOpenCrawlSummary={handleOpenCrawlSummary}
      />

      {pageState.kind === 'loading' ? (
        <LoadingSkeleton />
      ) : pageState.kind === 'no-access' ? (
        <NoAccessBanner />
      ) : pageState.kind === 'empty' ? (
        <EmptyState onGenerate={handleRegenerate} pending={triggerCrawlPending} />
      ) : pageState.kind === 'ready' ? (
        <CaseIntelligenceDashboard
          artefacts={pageState.artefacts}
          tagSuggestions={caseTagSuggestions}
          tagSuggestionsPending={caseTagSuggestionsPending}
          tagSuggestionsFeedback={caseTagSuggestionsFeedback}
          onRetryTags={handleRetryTags}
          onAcceptTagSuggestion={acceptCaseTagSuggestion}
          onRejectTagSuggestion={rejectCaseTagSuggestion}
        />
      ) : null}

      <CrawlSummaryDrawer open={isCrawlSummaryOpen} onClose={handleCloseCrawlSummary} />
    </div>
  );
};

export default CaseIntelligenceVibrantPage;
